/**
 * Extracts the duration (in seconds) from an audio Blob using the Web Audio API.
 * Rounds to 3 decimals for compact metadata storage.
 * @param {Blob} blob
 * @returns {Promise<number>} duration in seconds
 */
export async function extractAudioDurationFromBlob(blob) {
  if (!blob) {
    throw new Error('extractAudioDurationFromBlob: blob is required')
  }
  const arrayBuffer = await blob.arrayBuffer()
  const AudioContextClass = window.AudioContext || window.webkitAudioContext
  if (!AudioContextClass) {
    throw new Error('Web Audio API is not supported in this environment')
  }
  const audioContext = new AudioContextClass()
  try {
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer)
    const duration = typeof audioBuffer.duration === 'number' ? audioBuffer.duration : 0
    return Math.round(duration * 1000) / 1000
  } finally {
    try {
      await audioContext.close()
    } catch (error) {
      // ignore
    }
  }
}
/**
 * Extracts a volume profile (array of floats 0..1) from an AudioBuffer.
 *
 * @param {AudioBuffer} audioBuffer
 * @param {number} [bins=100]
 * @returns {number[]}
 */
export function extractVolumeProfileFromAudioBuffer(audioBuffer, bins = 100) {
  if (!audioBuffer || typeof audioBuffer.length !== 'number') {
    throw new Error('extractVolumeProfileFromAudioBuffer: invalid AudioBuffer')
  }
  const length = audioBuffer.length
  const channels = Math.max(1, audioBuffer.numberOfChannels || 1)
  const binsCount = Math.max(1, Math.floor(bins || 100))

  const channelData = []
  for (let channelIndex = 0; channelIndex < channels; channelIndex++) {
    // Ensure we always have data for the channel index; some buffers can report more channels
    // than data arrays available depending on decoder edge cases.
    try {
      channelData.push(audioBuffer.getChannelData(channelIndex))
    } catch (error) {
      // Fallback to zeros if channel data cannot be retrieved
      channelData.push(new Float32Array(length))
    }
  }

  const values = new Array(binsCount)
  let globalMax = 0

  for (let binIndex = 0; binIndex < binsCount; binIndex++) {
    const start = Math.floor((binIndex * length) / binsCount)
    let end = Math.floor(((binIndex + 1) * length) / binsCount)
    if (end <= start) {
      end = Math.min(length, start + 1)
    }

    const sampleCount = Math.max(0, end - start)
    let amplitude = 0

    if (sampleCount > 0) {
      // RMS across channels
      let sumSquares = 0
      for (let channelIndex = 0; channelIndex < channels; channelIndex++) {
        const channelSamples = channelData[channelIndex]
        for (let sampleIndex = start; sampleIndex < end; sampleIndex++) {
          const sampleValue = channelSamples[sampleIndex]
          sumSquares += sampleValue * sampleValue
        }
      }
      const meanSquare = sumSquares / (sampleCount * channels)
      amplitude = Math.sqrt(meanSquare)

      // Clamp to [0, 1] before normalization
      if (amplitude < 0) {
        amplitude = 0
      } else if (amplitude > 1) {
        amplitude = 1
      }
    }

    values[binIndex] = amplitude
    if (amplitude > globalMax) {
      globalMax = amplitude
    }
  }

  if (globalMax > 0) {
    for (let binIndex = 0; binIndex < binsCount; binIndex++) {
      values[binIndex] = Math.min(1, values[binIndex] / globalMax)
    }
  }

  // Round to 3 decimal places to avoid storing full-precision floats
  for (let binIndex = 0; binIndex < binsCount; binIndex++) {
    values[binIndex] = Math.round(values[binIndex] * 1000) / 1000
  }

  return values
}

/**
 * Extracts a volume profile (array of floats 0..1) from an audio Blob.
 * - Decodes the Blob using Web Audio API
 * - Splits the buffer into N bins (default 100)
 * - Computes per-bin RMS amplitude across all channels
 * - Normalizes to the maximum bin value
 *
 * @param {Blob} blob - The uploaded audio as a Blob
 * @param {number} [bins=100] - Number of bins to compute
 * @returns {Promise<number[]>} A Promise resolving to an array of length `bins` with values in [0, 1]
 */
export async function extractVolumeProfileFromBlob(blob, bins = 100) {
  if (!blob) {
    throw new Error('extractVolumeProfileFromBlob: blob is required')
  }

  const AudioContextClass = window.AudioContext || window.webkitAudioContext
  if (!AudioContextClass) {
    throw new Error('Web Audio API is not supported in this environment')
  }

  const audioContext = new AudioContextClass()
  const arrayBuffer = await blob.arrayBuffer()
  let audioBuffer
  try {
    // decodeAudioData returns a Promise in modern browsers
    audioBuffer = await audioContext.decodeAudioData(arrayBuffer)
  } finally {
    try {
      await audioContext.close()
    } catch (error) {
      // Ignore close errors (Safari may throw)
    }
  }

  return extractVolumeProfileFromAudioBuffer(audioBuffer, bins)
}

/**
 * Resamples a normalized volume profile (array of numbers in [0,1])
 * to a target number of bins using a weighted average strategy.
 * Example:
 *  originalVolumes: [1, 1, 0, 1]
 *  targetCount: 2
 *  returns: [1, 0.5]
 *
 * @param {number[]} originalVolumes - Source volume array (ideally normalized to [0,1])
 * @param {number} targetCount - Target number of bars/bins to compute
 * @returns {number[]} A new array of length `targetCount` with resampled values
 */
export function resampleVolumeProfile(originalVolumes, targetCount) {
  const sourceLength = originalVolumes.length
  const destinationLength = Math.max(1, Math.floor(targetCount))
  if (sourceLength === 0) {
    return []
  }
  if (sourceLength === destinationLength) {
    return originalVolumes.slice()
  }

  const result = new Array(destinationLength)
  // Map each destination bin to a weighted average of source bins
  for (let destinationIndex = 0; destinationIndex < destinationLength; destinationIndex++) {
    // Basically for each destination bin, we take the average of the source bins that we want to sample it.
    // E.g., if we sample from 100 to 50 bins, the first destination bin will be the average of bin 1 and 2, and so on.
    const startPosition = (destinationIndex * sourceLength) / destinationLength
    const endPosition = ((destinationIndex + 1) * sourceLength) / destinationLength
    const startIndex = Math.floor(startPosition)
    const endIndex = Math.floor(endPosition)

    let value
    if (startIndex === endIndex) {
      // Entire bin falls within a single source index
      value = originalVolumes[startIndex]
    } else {
      let sum = 0
      let totalWeight = 0

      // First partial segment
      const firstWeight = 1 - (startPosition - startIndex)
      sum += originalVolumes[startIndex] * firstWeight
      totalWeight += firstWeight

      // Full segments in between
      for (let sourceIndex = startIndex + 1; sourceIndex < endIndex; sourceIndex++) {
        sum += originalVolumes[sourceIndex]
        totalWeight += 1
      }

      // Last partial segment
      const lastWeight = endPosition - endIndex
      if (lastWeight > 0 && endIndex < sourceLength) {
        sum += originalVolumes[Math.min(endIndex, sourceLength - 1)] * lastWeight
        totalWeight += lastWeight
      }

      value = totalWeight > 0 ? sum / totalWeight : 0
    }

    result[destinationIndex] = value
  }

  return result
}
